import { Link } from "react-router-dom";
import type { Phase } from "@shared/types";
import { useT } from "../i18n";

/** Section header: uppercase title with an optional right-aligned link. */
export function SectionHeader({ title, moreTo, moreLabel }: { title: string; moreTo?: string; moreLabel?: string }) {
  const t = useT();
  return (
    <div className="sec-h">
      <h2>{title}</h2>
      {moreTo && (
        <Link to={moreTo} className="more">
          {moreLabel ?? `${t("common.seeAll")} →`}
        </Link>
      )}
    </div>
  );
}

// Page is just the routed content container now — the crest/header lives in the
// persistent <Nav> rendered above the routes.
export function Page({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1120px] mx-auto px-5 pt-7 pb-24">{children}</div>;
}

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`card-x ${className}`}>{children}</div>;
}

export function Bar({ pct, color = "#f97316" }: { pct: number; color?: string }) {
  return (
    <div className="h-1.5 bg-panel2 rounded overflow-hidden mt-1">
      <div
        className="h-full rounded transition-all duration-500"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }}
      />
    </div>
  );
}

export function PhasePill({ phase, startsAt }: { phase: Phase; startsAt?: string }) {
  const t = useT();
  if (phase === "live")
    return (
      <span className="ph ph-live">
        <span className="dot" /> {t("phase.live")}
      </span>
    );
  if (phase === "soon") return <span className="ph ph-soon">{startsAt ? countdownLabel(startsAt, t) : t("phase.soon")}</span>;
  return <span className="ph ph-done">{t("phase.done")}</span>;
}

/** Compact countdown for the "soon" pill, e.g. "IN 3D" / "IN 5H" / "IN 12M". */
function countdownLabel(iso: string, t: TFn, nowMs: number = Date.now()): string {
  const ms = Date.parse(iso) - nowMs;
  if (ms <= 0) return t("phase.soon");
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return t("phase.inMinutes", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("phase.inHours", { n: hrs });
  return t("phase.inDays", { n: Math.floor(hrs / 24) });
}

type TFn = ReturnType<typeof useT>;

export function StarButton({ active, onClick, title }: { active: boolean; onClick: () => void; title?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      title={title ?? (active ? t("common.removeFavorite") : t("common.addFavorite"))}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={`text-lg leading-none transition ${active ? "text-accent" : "text-muted hover:text-accent"}`}
      aria-pressed={active}
    >
      {active ? "★" : "☆"}
    </button>
  );
}

export function Spinner({ label }: { label?: string }) {
  const t = useT();
  return <div className="text-muted text-sm py-10 text-center">{label ?? t("common.loading")}</div>;
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-panel2 rounded ${className}`} />;
}

/** Rich loading panel: animated pulse, headline, rotating/explanatory message,
 * and an optional real progress bar. Caller supplies the (already-resolved)
 * strings and pct; cycling and status polling live in the page. */
export function RichLoader({ headline, message, pct }: { headline: string; message: string; pct?: number }) {
  return (
    <div className="card-x my-4 text-center py-10">
      <div className="mx-auto mb-4 h-3 w-3 rounded-full bg-accent animate-ping" />
      <p className="text-sm font-medium mb-1">{headline}</p>
      <p className="text-muted text-xs mb-3">{message}</p>
      {pct !== undefined && (
        <div className="max-w-[220px] mx-auto">
          <Bar pct={pct} />
        </div>
      )}
    </div>
  );
}

/** Loading placeholder shaped like the forecast page. */
export function ForecastSkeleton() {
  return (
    <div className="space-y-4">
      <div className="my-4 space-y-2">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
      <Card>
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0">
            <div className="space-y-2 w-1/2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-12" />
          </div>
        ))}
      </Card>
      <Card><Skeleton className="h-24 w-full" /></Card>
    </div>
  );
}

export function UpdatedAt({ ms }: { ms: number | null }) {
  const t = useT();
  if (!ms) return null;
  return <div className="text-[11px] text-muted mb-3">{t("common.updatedAgo", { ago: timeAgo(ms, t) })}</div>;
}

export function timeAgo(ms: number, t: TFn): string {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return t("common.justNow");
  if (mins < 60) return t("common.minutesAgo", { n: mins });
  if (mins < 1440) return t("common.hoursAgo", { n: Math.floor(mins / 60) });
  return t("common.daysAgo", { n: Math.floor(mins / 1440) });
}

export function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="text-warn text-sm bg-warn/10 border border-warn/25 rounded-lg p-3">{msg}</div>
  );
}
